import { useState, useRef, useEffect } from "react";
import { User, Sun, Moon, LogOut } from "lucide-react";
import { useRouter } from "@tanstack/react-router";

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface ProfileAvatarMenuProps {
  name: string;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  onLogout: () => void;
}

export function ProfileAvatarMenu({
  name,
  theme,
  onToggleTheme,
  onLogout,
}: ProfileAvatarMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Avatar button */}
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Perfil de ${name}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "44px",
          height: "44px",
          borderRadius: "50%",
          border: `1.5px solid ${open ? "var(--shell-green)" : "var(--shell-line)"}`,
          background: "var(--shell-panel)",
          color: "var(--shell-text)",
          fontFamily: "'DM Mono', monospace",
          fontSize: "0.75rem",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "border-color 0.15s, background 0.15s",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          if (!open) {
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              "var(--shell-green)";
            (e.currentTarget as HTMLButtonElement).style.background =
              "var(--shell-panel)";
          }
        }}
        onMouseLeave={(e) => {
          if (!open) {
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              "var(--shell-line)";
            (e.currentTarget as HTMLButtonElement).style.background =
              "var(--shell-panel)";
          }
        }}
      >
        {getInitials(name)}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="app-shell-profile-dropdown">
          {/* Meu Perfil */}
          <button
            type="button"
            className="app-shell-profile-item"
            onClick={() => {
              setOpen(false);
              router.navigate({ to: "/perfil" });
            }}
          >
            <User size={15} strokeWidth={1.5} />
            Meu Perfil
          </button>

          {/* Aparência */}
          <button
            type="button"
            className="app-shell-profile-item"
            onClick={() => {
              onToggleTheme();
            }}
          >
            {theme === "dark" ? (
              <Sun size={15} strokeWidth={1.5} />
            ) : (
              <Moon size={15} strokeWidth={1.5} />
            )}
            Aparência
          </button>

          <div className="app-shell-profile-divider" />

          {/* Sair */}
          <button
            type="button"
            className="app-shell-profile-item app-shell-profile-item--logout"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <LogOut size={15} strokeWidth={1.5} />
            Sair
          </button>
        </div>
      )}
    </div>
  );
}
